import { useEffect } from 'react';
import { Datepicker, Input, useLocale } from 'shared-ui';
import { Controller, useFormContext } from 'react-hook-form';

import { getNow } from 'helpers/getNow';
import { handleValidFromDate } from 'helpers/handleValidFromDate';
import { handleValidToDate } from 'helpers/handleValidToDate';
import { Form } from 'ui/form/Form';
import { ReportModal } from '../reportModal/ReportModal';
import { OrganizationsTreeSelect } from 'ui/organizationsTreeSelect/OrganizationsTreeSelect';
import { AppMessages } from 'i18n/messages';
import { ReactComponent as ReportOwnersIcon } from 'assets/icons/report-owners.svg';
import { ReportTile } from '../reportTile/ReportTile';
import { usePermissions } from 'hooks/usePermissions/usePermissions';
import * as Styled from '../Reports.styles';
import { ReportErrorAlert } from '../reportErrorAlert';

import { OwnersWithoutEventsReportProps, OwnersWithoutEventsReportForm } from './OwnersWithoutEventsReport.types';

export const OwnersWithoutEventsReport = ({
  isModalOpen,
  onModalClose,
  onModalOpen,
  onDownload,
  isLoadingDownload,
  hasNoDataError,
  onEmailModalOpen,
  isEmailModalOpen,
  isSmtpConfigured,
  onResetError,
}: OwnersWithoutEventsReportProps) => {
  const { formatMessage } = useLocale();
  const { perms } = usePermissions();

  const {
    control,
    formState: { errors },
    watch,
    trigger,
  } = useFormContext<OwnersWithoutEventsReportForm>();

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (hasNoDataError && name) {
        onResetError();
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, hasNoDataError, onResetError]);

  return (
    <>
      {perms?.RAPORT_POSIADACZY_BEZ_ZDARZEN?.view && (
        <ReportTile
          icon={<ReportOwnersIcon />}
          name={formatMessage({ id: AppMessages['reports.ownersWithoutEventsReport'] })}
          onClick={onModalOpen}
        />
      )}

      <ReportModal
        header={formatMessage({ id: AppMessages['reports.ownersWithoutEventsReport.title'] })}
        onClose={onModalClose}
        open={isModalOpen}
        onSend={onEmailModalOpen}
        onDownload={() => onDownload()}
        isLoading={isLoadingDownload}
        isSendButtonDisabled={isLoadingDownload || !isSmtpConfigured || Object.keys(errors).length > 0}
        isDownloadButtonDisabled={isLoadingDownload || Object.keys(errors).length > 0}
      >
        <Form.Grid gridTemplateColumns="1fr 1fr">
          <Controller
            name="dataOd"
            control={control}
            render={({ field }) => {
              const dataDo = watch('dataDo');
              const maxAllowed = dataDo || new Date();

              return (
                <Datepicker
                  {...field}
                  onChange={(date) => {
                    field.onChange(date);
                    trigger('dataDo');
                  }}
                  label={formatMessage({ id: AppMessages['common.from'] })}
                  placeholderText={formatMessage({
                    id: AppMessages['reports.ownersWithoutEventsReport.fromDate.placeholder'],
                  })}
                  withCalendarIcon
                  withDatepickerPopover
                  isClickable
                  maxDate={maxAllowed}
                  errorMessage={errors?.dataOd?.message}
                  popoverOrigin={{
                    vertical: -80,
                    horizontal: 60,
                  }}
                  mediumPlaceholderFontSize
                  customSetDateFormat={handleValidFromDate}
                  customDateFormatOnOpen={() => field.value || getNow(maxAllowed)}
                  withoutMinDateError
                />
              );
            }}
          />

          <Controller
            name="dataDo"
            control={control}
            render={({ field }) => {
              const dataOd = watch('dataOd');

              return (
                <Datepicker
                  {...field}
                  onChange={(date) => {
                    field.onChange(date);
                    trigger('dataOd');
                  }}
                  label={formatMessage({ id: AppMessages['common.to'] })}
                  placeholderText={formatMessage({
                    id: AppMessages['reports.ownersWithoutEventsReport.toDate.placeholder'],
                  })}
                  minDate={dataOd || undefined}
                  maxDate={new Date()}
                  withCalendarIcon
                  withDatepickerPopover
                  isClickable
                  errorMessage={errors?.dataDo?.message}
                  popoverOrigin={{
                    vertical: -80,
                    horizontal: 60,
                  }}
                  mediumPlaceholderFontSize
                  customSetDateFormat={handleValidToDate}
                  customDateFormatOnOpen={() => field.value || getNow()}
                  withoutMinDateError
                />
              );
            }}
          />

          <Controller
            name="nazwisko"
            control={control}
            render={({ field }) => (
              <Input
                label={formatMessage({ id: AppMessages['reports.eventsReport.lastName'] })}
                placeholder={formatMessage({
                  id: AppMessages['reports.ownersWithoutEventsReport.lastName.placeholder'],
                })}
                {...field}
              />
            )}
          />

          <Controller
            name="numerKarty"
            control={control}
            render={({ field }) => (
              <Input
                label={formatMessage({ id: AppMessages['reports.eventsReport.cardNumber'] })}
                placeholder={formatMessage({
                  id: AppMessages['reports.ownersWithoutEventsReport.cardNumber.placeholder'],
                })}
                {...field}
              />
            )}
          />

          <Styled.FieldContainer>
            <Controller
              name="komorka"
              control={control}
              render={({ field }) => (
                <OrganizationsTreeSelect
                  {...field}
                  multiple={false}
                  placeholder={formatMessage({ id: AppMessages['reports.ownersWithoutEventsReport.cell.placeholder'] })}
                  label={formatMessage({ id: AppMessages['reports.eventsReport.cell'] })}
                />
              )}
            />
          </Styled.FieldContainer>
        </Form.Grid>

        {hasNoDataError && !isEmailModalOpen && (
          <ReportErrorAlert message={formatMessage({ id: AppMessages['reports.error.noData'] })} />
        )}
      </ReportModal>
    </>
  );
};
